import type { ReactNode } from 'react';
import type { TraderTransaction } from '@/types/api';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { maskedCard, rub, usdt } from '@/lib/format';

/**
 * A misclick here releases USDT against money that may not have arrived, so the
 * dialog restates the three facts the trader must check against their banking
 * app — amount, card, merchant — before the action is available.
 */
export function ConfirmDialog({
  transaction,
  open,
  isSubmitting,
  onCancel,
  onConfirm,
}: {
  transaction: TraderTransaction | null;
  open: boolean;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!transaction) return null;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      busy={isSubmitting}
      size="sm"
      title="Подтвердите, что платёж пришёл"
      description="Сверьте эти данные с банком, прежде чем подтверждать. Подтверждение выпускает USDT и не отменяется."
      footer={
        <>
          <Button onClick={onCancel} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button variant="confirm" onClick={onConfirm} isLoading={isSubmitting}>
            {isSubmitting ? 'Подтверждаю…' : 'Да, деньги пришли'}
          </Button>
        </>
      }
    >
      <dl className="divide-y divide-line rounded-md border border-line">
        <Row label="Сумма поступления">
          <span className="num whitespace-nowrap text-md font-semibold text-fg">
            {rub(transaction.amountRub)}
          </span>
        </Row>
        <Row label="На карту">
          <span className="num text-base text-fg">
            {transaction.cardBank ? `${transaction.cardBank} · ` : ''}
            {maskedCard(transaction.cardLast4)}
          </span>
        </Row>
        <Row label="От мерчанта">
          <span className="text-base text-fg">{transaction.merchantName}</span>
        </Row>
        <Row label="Номер платежа">
          <span className="num text-xs text-fg-secondary">{transaction.reference}</span>
        </Row>
        {/* This figure is the collateral released to the merchant, not the
            trader payout it used to be labelled as. Conflating the two is how
            a trader ends up expecting the whole amount to land in their
            withdrawable balance. */}
        <Row label="Уйдёт мерчанту из залога">
          <span className="num whitespace-nowrap text-base text-fg-secondary">
            {usdt(transaction.amountUsdt)}
          </span>
        </Row>
        {transaction.commissionUsdt === undefined ? null : (
          <Row label="Ваша комиссия">
            <span className="num whitespace-nowrap text-base font-medium text-fg">
              {transaction.traderMarkupRub != null ? rub(transaction.traderMarkupRub) : usdt(transaction.commissionUsdt)}
            </span>
          </Row>
        )}
      </dl>

      <p className="mt-3 text-xs leading-normal text-fg-muted">
        Если сумма в банке отличается от указанной, не подтверждайте — нажмите «Деньги не пришли» и
        опишите расхождение.
      </p>
    </Dialog>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-3 py-2.5">
      {/* Not `shrink-0`: the Russian labels are long enough that on a 320px
          screen a fixed label plus a seven-figure amount would overflow. */}
      <dt className="min-w-0 text-sm text-fg-muted">{label}</dt>
      <dd className="min-w-0 break-all text-right">{children}</dd>
    </div>
  );
}

